import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaComments, 
  FaTimes, 
  FaPaperPlane, 
  FaRobot, 
  FaUser,
  FaQuestionCircle,
  FaChevronDown,
  FaPhone,
  FaEnvelope
} from 'react-icons/fa';
import contentData from '../../data/contentData.json';

const SupportChat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentView, setCurrentView] = useState('main'); // 'main', 'faqs', 'contact'
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: `Hi! I'm your virtual assistant. How can I help you today?`,
      sender: 'bot',
      timestamp: new Date(),
      options: ['View FAQs', 'Contact Support', 'General Inquiry']
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [selectedFaq, setSelectedFaq] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(scrollToBottom, [messages]);

  const chatVariants = {
    hidden: { 
      opacity: 0, 
      scale: 0,
      y: 100 
    },
    visible: { 
      opacity: 1, 
      scale: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 20
      }
    },
    exit: { 
      opacity: 0, 
      scale: 0,
      y: 100,
      transition: {
        duration: 0.2
      }
    }
  };

  const messageVariants = {
    hidden: { opacity: 0, y: 20, scale: 0.8 },
    visible: { 
      opacity: 1, 
      y: 0, 
      scale: 1,
      transition: {
        type: 'spring',
        stiffness: 400,
        damping: 25
      }
    }
  };

  const bubbleVariants = {
    hidden: { scale: 0 },
    visible: { 
      scale: 1,
      transition: {
        type: 'spring',
        stiffness: 400,
        damping: 15
      }
    },
    pulse: {
      scale: [1, 1.05, 1],
      transition: {
        duration: 2,
        repeat: Infinity,
        ease: 'easeInOut'
      }
    }
  };

  const handleSendMessage = (text = inputValue) => {
    if (!text.trim()) return;

    const userMessage = {
      id: Date.now(),
      text: text,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);

    // Simulate bot response
    setTimeout(() => {
      let botResponse = {
        id: Date.now() + 1,
        text: "I understand you're looking for help. Let me connect you with the right information.",
        sender: 'bot',
        timestamp: new Date()
      };

      // Simple keyword matching for demo
      const lowerText = text.toLowerCase();
      if (lowerText.includes('pricing') || lowerText.includes('cost')) {
        botResponse.text = "For pricing information, I'd recommend checking our services page or contacting our sales team directly.";
        botResponse.options = ['View Services', 'Contact Sales'];
      } else if (lowerText.includes('aws') || lowerText.includes('cloud')) {
        botResponse.text = "Great! We specialize in AWS cloud services. Would you like to know more about our specific offerings?";
        botResponse.options = ['AWS Migration', 'Cloud Security', 'Cost Management'];
      } else if (lowerText.includes('contact') || lowerText.includes('support')) {
        botResponse.text = "I can help you get in touch with our team. What's the best way to reach you?";
        botResponse.options = ['Phone Call', 'Email', 'Schedule Meeting'];
      }

      setMessages(prev => [...prev, botResponse]);
      setIsTyping(false);
    }, 1500);
  };

  const handleOptionClick = (option) => {
    handleSendMessage(option);
    
    if (option === 'View FAQs') {
      setCurrentView('faqs');
    } else if (option === 'Contact Support') {
      setCurrentView('contact');
    }
  };

  const renderEyesAnimation = () => (
    <motion.div className="flex space-x-1">
      <motion.div
        className="w-1.5 h-1.5 bg-blue-600 rounded-full"
        animate={{
          scaleY: [1, 0.1, 1],
        }}
        transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />
      <motion.div
        className="w-1.5 h-1.5 bg-blue-600 rounded-full"
        animate={{
          scaleY: [1, 0.1, 1],
        }}
        transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 0.1
        }}
      />
    </motion.div>
  );

  const renderMainChat = () => (
    <div className="flex flex-col h-full">
      {/* Chat Header */}
      <div className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-t-2xl">
        <div className="flex items-center space-x-3">
          <motion.div 
            className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center relative"
            animate={{
              rotate: [0, 5, -5, 0],
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: 'easeInOut'
            }}
          >
            <FaRobot className="text-lg" />
            <div className="absolute -top-1 -right-1">
              {renderEyesAnimation()}
            </div>
          </motion.div>
          <div>
            <h3 className="font-semibold">Support Assistant</h3>
            <p className="text-xs text-blue-100">Online now</p>
          </div>
        </div>
        <button 
          onClick={() => setIsOpen(false)}
          className="p-2 hover:bg-white/20 rounded-full transition-colors"
        >
          <FaTimes className="text-lg" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
        <AnimatePresence>
          {messages.map((message) => (
            <motion.div
              key={message.id}
              variants={messageVariants}
              initial="hidden"
              animate="visible"
              className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-2xl ${
                message.sender === 'user' 
                  ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white' 
                  : 'bg-white shadow-md border border-gray-100'
              }`}>
                <p className="text-sm">{message.text}</p>
                {message.options && (
                  <div className="mt-3 space-y-2">
                    {message.options.map((option, index) => (
                      <motion.button
                        key={index}
                        onClick={() => handleOptionClick(option)}
                        className="block w-full text-left px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg text-xs font-medium transition-colors"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        {option}
                      </motion.button>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </AnimatePresence>

        {isTyping && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex justify-start"
          >
            <div className="bg-white shadow-md border border-gray-100 rounded-2xl px-4 py-2">
              <div className="flex space-x-1">
                <motion.div
                  className="w-2 h-2 bg-gray-400 rounded-full"
                  animate={{ y: [0, -5, 0] }}
                  transition={{ duration: 0.6, repeat: Infinity, delay: 0 }}
                />
                <motion.div
                  className="w-2 h-2 bg-gray-400 rounded-full"
                  animate={{ y: [0, -5, 0] }}
                  transition={{ duration: 0.6, repeat: Infinity, delay: 0.2 }}
                />
                <motion.div
                  className="w-2 h-2 bg-gray-400 rounded-full"
                  animate={{ y: [0, -5, 0] }}
                  transition={{ duration: 0.6, repeat: Infinity, delay: 0.4 }}
                />
              </div>
            </div>
          </motion.div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-4 bg-white border-t border-gray-100 rounded-b-2xl">
        <div className="flex space-x-2">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
            placeholder="Type your message..."
            className="flex-1 px-4 py-2 border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <motion.button
            onClick={() => handleSendMessage()}
            className="p-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-full hover:shadow-lg transition-shadow"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <FaPaperPlane className="text-sm" />
          </motion.button>
        </div>
      </div>
    </div>
  );

  const renderFAQs = () => (
    <div className="flex flex-col h-full">
      {/* FAQ Header */}
      <div className="flex items-center justify-between p-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-t-2xl">
        <h3 className="font-semibold">Frequently Asked Questions</h3>
        <button 
          onClick={() => setCurrentView('main')}
          className="p-2 hover:bg-white/20 rounded-full transition-colors"
        >
          <FaTimes className="text-lg" />
        </button>
      </div>

      {/* FAQ Content */}
      <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
        <div className="space-y-3">
          {contentData.faqs.map((faq, index) => (
            <motion.div
              key={index}
              className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <button
                onClick={() => setSelectedFaq(selectedFaq === index ? null : index)}
                className="w-full px-4 py-3 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <span className="font-medium text-gray-900">{faq.question}</span>
                <motion.div
                  animate={{ rotate: selectedFaq === index ? 180 : 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <FaChevronDown className="text-gray-400" />
                </motion.div>
              </button>
              <AnimatePresence>
                {selectedFaq === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-4 pb-3 text-gray-600 text-sm leading-relaxed">
                      {faq.answer}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>

      {/* FAQ Footer */}
      <div className="p-4 bg-white border-t border-gray-100 rounded-b-2xl">
        <button
          onClick={() => setCurrentView('contact')}
          className="w-full px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-medium hover:shadow-lg transition-shadow"
        >
          Still need help? Contact us
        </button>
      </div>
    </div>
  );

  const renderContact = () => (
    <div className="flex flex-col h-full">
      {/* Contact Header */}
      <div className="flex items-center justify-between p-4 bg-gradient-to-r from-green-600 to-blue-600 text-white rounded-t-2xl">
        <h3 className="font-semibold">Contact Support</h3>
        <button 
          onClick={() => setCurrentView('main')}
          className="p-2 hover:bg-white/20 rounded-full transition-colors"
        >
          <FaTimes className="text-lg" />
        </button>
      </div>

      {/* Contact Content */}
      <div className="flex-1 p-6 bg-gray-50">
        <div className="space-y-4">
          <motion.a
            href={`tel:${contentData.company.contact.phone}`}
            className="flex items-center p-4 bg-white rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mr-4">
              <FaPhone className="text-green-600" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900">Call Us</h4>
              <p className="text-gray-600 text-sm">{contentData.company.contact.phone}</p>
            </div>
          </motion.a>

          <motion.a
            href={`mailto:${contentData.company.contact.email}`}
            className="flex items-center p-4 bg-white rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
              <FaEnvelope className="text-blue-600" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900">Email Us</h4>
              <p className="text-gray-600 text-sm">{contentData.company.contact.email}</p>
            </div>
          </motion.a>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Floating Chat Button */}
      <motion.div
        className="fixed bottom-6 right-6 z-50"
        variants={bubbleVariants}
        initial="hidden"
        animate={isOpen ? "visible" : "pulse"}
      >
        <motion.button
          onClick={() => setIsOpen(!isOpen)}
          className="w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-full shadow-2xl flex items-center justify-center hover:shadow-3xl transition-shadow relative overflow-hidden"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <AnimatePresence mode="wait">
            {isOpen ? (
              <motion.div
                key="close"
                initial={{ rotate: -90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: 90, opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <FaTimes className="text-xl" />
              </motion.div>
            ) : (
              <motion.div
                key="chat"
                initial={{ rotate: 90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: -90, opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <FaComments className="text-xl" />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Pulse ring */}
          <div className="absolute inset-0 rounded-full bg-blue-400 animate-ping opacity-20"></div>
        </motion.button>
      </motion.div>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="fixed bottom-24 right-6 w-80 h-96 bg-white rounded-2xl shadow-2xl z-50 border border-gray-100 overflow-hidden"
            variants={chatVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            {currentView === 'main' && renderMainChat()}
            {currentView === 'faqs' && renderFAQs()}
            {currentView === 'contact' && renderContact()}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default SupportChat; 